/* @jsxImportSource @emotion/react */

import { useEffect, useState } from 'react'
import { css, keyframes } from '@emotion/react'
import { useIntl } from 'react-intl'

import { Section } from '../../components/Section'
import { TextLayout } from '../../components/TextLayout'
import { useRoutes } from '../../routes'
import { ScenarioButtons } from '../home/Hero/ScenarioButtons'
import { SectionSolutionsAdvantage } from '../solutions/@slug/SectionSolutionsAdvantage'
import type { AgentSolution } from './+data'

const AUTO_SWITCH_MS = 6000

// Same slug → brand color mapping as the solution detail pages.
const BRAND_COLORS: Record<string, string> = {
  'it-operations': 'slite-blue-500',
  'product-engineering': 'moon-dust-darkest',
  'human-resources': 'slite-yellow-500',
  'customer-support': 'slite-pink-500',
  sales: 'slite-pink-500',
}
const DEFAULT_BRAND_COLOR = 'slite-blue-500'

// All cards stay mounted so their images are loaded before switching.
const slidesCss = css`
  display: grid;

  > * {
    grid-area: 1 / 1;
    min-width: 0;
  }
`

const riseIn = keyframes`
  from { transform: translateY(16px); }
`

const slideCss = css`
  opacity: 0;
  visibility: hidden;
  pointer-events: none;
  transition:
    opacity 0.35s ease,
    visibility 0s linear 0.35s;

  &[data-active='true'] {
    opacity: 1;
    visibility: visible;
    pointer-events: auto;
    transition:
      opacity 0.45s ease 0.15s,
      visibility 0s;
    animation: ${riseIn} 0.6s cubic-bezier(0.22, 1, 0.36, 1) 0.15s both;
  }

  @media (prefers-reduced-motion: reduce) {
    &[data-active='true'] {
      animation: none;
    }
  }
`

export function SectionAgentSolutions({
  solutions,
}: {
  solutions: AgentSolution[]
}) {
  const intl = useIntl()
  const routes = useRoutes()
  const [activeIdx, setActiveIdx] = useState(0)
  // Restarts the pill's fill animation on every switch, manual or automatic.
  const [playId, setPlayId] = useState(0)

  // Keyed on activeIdx: every switch, manual or automatic, restarts the timer.
  useEffect(() => {
    const timer = setTimeout(() => {
      setActiveIdx((activeIdx + 1) % solutions.length)
      setPlayId((id) => id + 1)
    }, AUTO_SWITCH_MS)
    return () => clearTimeout(timer)
  }, [activeIdx, solutions.length])

  if (!solutions[activeIdx]) return null

  return (
    <Section background="moon-dust-lighter" containerVerticalPadding={80}>
      <div className="text-align-center">
        <TextLayout
          type="centered"
          title={intl.formatMessage({
            id: 'agent.solutions.title',
            defaultMessage: 'See how every team puts Slite Agent to work',
          })}
          titleMaxWidth={500}
        />
      </div>
      <div className="margin-top-24 margin-bottom-16">
        <ScenarioButtons
          scenarios={solutions.map((solution) => ({
            id: solution.slug.current,
            buttonLabel: solution.name,
          }))}
          activeIdx={activeIdx}
          playId={playId}
          durationMs={AUTO_SWITCH_MS}
          onSelect={(idx) => {
            setActiveIdx(idx)
            setPlayId((id) => id + 1)
          }}
        />
      </div>
      <div css={slidesCss}>
        {solutions.map((solution, index) => {
          const advantage = solution.advantages[0]
          return (
            <div
              key={solution.slug.current}
              css={slideCss}
              data-active={index === activeIdx}
              aria-hidden={index !== activeIdx}
            >
              <SectionSolutionsAdvantage
                index={0}
                name={solution.name}
                title={advantage.title}
                description={advantage.description}
                imageUrl={advantage.image.url}
                quote={
                  advantage.quote
                    ? {
                        name: advantage.quote.name,
                        title: `${advantage.quote.title} at ${advantage.quote.company}`,
                        testimonial: advantage.quote.testimonial,
                        pictureUrl: advantage.quote.picture.url,
                      }
                    : null
                }
                brandColor={
                  BRAND_COLORS[solution.slug.current] ?? DEFAULT_BRAND_COLOR
                }
                cta={{
                  href: routes.solutions.detail({
                    slug: solution.slug.current,
                  }),
                  label: intl.formatMessage({
                    id: 'agent.solutions.cta',
                    defaultMessage: 'Read more',
                  }),
                }}
              />
            </div>
          )
        })}
      </div>
    </Section>
  )
}
